import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser, useClerk } from "@clerk/clerk-react";
import { toast } from "sonner";
import {
  raiseBoardApi,
  type BoardScope,
  type RaiseBoardBySlugResponse,
  type RaiseBoardGlobalApplication,
  type RaiseBoardGlobalResponse,
} from "@/api/raise-board-api";
import { axiosInstance } from "@/lib/axios-instance";
import { useWorkspaceStore } from "@/store/workspace-store";
import { getErrorMessage } from "@/lib/api-error";
import { getVisitorId } from "@/lib/lookbook-access";

const GLOBAL_KEY = ["raise-board-global"] as const;

/**
 * Vote lists are per-user, so the cache key carries the user id.
 *
 * Without it, switching accounts in the same tab reads the previous user's
 * votes: the board sign-in dialog completes email OTP without reloading the
 * page, nothing clears the query cache on auth change, and gcTime is 30
 * minutes. That stale list also drove the optimistic delta below, so the first
 * click under the new account could move the count the wrong way before the
 * server corrected it.
 */
function myVotesKey(
  userId: string | null | undefined,
  lookbookPublicId?: string
) {
  // Scope is part of the key, not just the request: the same user has a
  // different set of voted deals inside each lookbook than on the public board,
  // and sharing one cache entry would light up the wrong rows.
  return [
    "raise-board-my-votes",
    userId ?? "anon",
    lookbookPublicId ?? "global",
  ] as const;
}

/** The cache holding one board's applications, whichever board that is. */
function boardKey(slug?: string, lookbookPublicId?: string) {
  if (lookbookPublicId) return ["lookbook", lookbookPublicId] as const;
  if (slug) return ["raise-board", slug] as const;
  return null;
}

/**
 * Who is voting, for the cache key: the signed-in user, or — on a lookbook
 * that allows guests — this browser's visitor id, prefixed so the two can
 * never share an entry.
 */
function voterId(userId: string | null | undefined, guest: boolean) {
  return guest ? `guest:${getVisitorId()}` : userId;
}

/**
 * The signed-in user's upvoted application ids. The boards themselves are read
 * anonymously, so which deals *this* user voted on comes from a separate call.
 *
 * Pass `lookbookPublicId` from a lookbook so it asks for that lookbook's votes rather
 * than the public ones, and `guest` when a signed-out visitor is voting there
 * as a guest.
 */
export function useMyVotes(
  enabled: boolean,
  lookbookPublicId?: string,
  guest = false
): Set<string> {
  const { user } = useUser();
  const { data } = useQuery({
    queryKey: myVotesKey(voterId(user?.id, guest), lookbookPublicId),
    queryFn: () =>
      guest && lookbookPublicId
        ? raiseBoardApi.getGuestVotes(lookbookPublicId, getVisitorId())
        : raiseBoardApi.getMyVotes(lookbookPublicId),
    // No user id means no votes to fetch — and the "anon" key keeps a signed
    // out visitor from reading whatever the last account cached.
    enabled: enabled && (guest ? !!lookbookPublicId : !!user?.id),
    // Refresh alongside the board so voted state can't lag behind the counts
    // (e.g. after voting from another tab).
    refetchOnWindowFocus: true,
  });
  return useMemo(() => new Set(data ?? []), [data]);
}

/**
 * Optimistically toggles a vote across every cache that might hold the deal,
 * rolls back on failure, and refetches on settle so counts and ranking
 * reconcile with the server.
 *
 * Pass `slug` from the deal profile page so the ["raise-board", slug] cache is
 * kept in step; the global board omits it and only the global cache is touched.
 * Each setQueryData is a no-op when that cache isn't populated.
 *
 * Pass `lookbookPublicId` instead from a lookbook. That scope is exclusive: a
 * lookbook vote must not move the number the global board shows, so the global
 * cache is left alone entirely rather than optimistically patched and then
 * corrected by a refetch.
 *
 * `guest` votes as this browser's visitor rather than the signed-in user; it
 * only means anything alongside `lookbookPublicId`.
 */
export function useToggleUpvote({
  slug,
  lookbookPublicId,
  guest = false,
}: { slug?: string; lookbookPublicId?: string; guest?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const votesKey = myVotesKey(voterId(user?.id, guest), lookbookPublicId);
  const scopedKey = boardKey(slug, lookbookPublicId);
  // The global board shares its tally with the per-workspace boards, so it is
  // in scope for a public vote and out of scope for a lookbook one.
  const touchesGlobal = !lookbookPublicId;

  /** Rewrite one deal's count in whichever board caches hold it. */
  const writeCount = (
    applicationId: string,
    next: (current: number) => number
  ) => {
    const apply = (list: RaiseBoardGlobalApplication[]) =>
      list.map((a) =>
        String(a._id) === applicationId
          ? { ...a, upvote_count: Math.max(0, next(a.upvote_count ?? 0)) }
          : a
      );

    if (touchesGlobal) {
      queryClient.setQueryData<RaiseBoardGlobalResponse>(GLOBAL_KEY, (old) =>
        old
          ? {
              ...old,
              result: {
                ...old.result,
                applications: apply(old.result.applications),
              },
            }
          : old
      );
    }

    if (scopedKey) {
      queryClient.setQueryData<RaiseBoardBySlugResponse>(scopedKey, (old) =>
        old
          ? {
              ...old,
              result: {
                ...old.result,
                applications: apply(old.result.applications),
              },
            }
          : old
      );
    }
  };

  return useMutation({
    mutationFn: (applicationId: string) =>
      guest && lookbookPublicId
        ? raiseBoardApi.toggleGuestVote(
            applicationId,
            lookbookPublicId,
            getVisitorId()
          )
        : raiseBoardApi.toggleVote(applicationId, lookbookPublicId),
    onMutate: async (applicationId: string) => {
      if (touchesGlobal) {
        await queryClient.cancelQueries({ queryKey: GLOBAL_KEY });
      }
      await queryClient.cancelQueries({ queryKey: votesKey });
      if (scopedKey) await queryClient.cancelQueries({ queryKey: scopedKey });

      const prevGlobal = touchesGlobal
        ? queryClient.getQueryData<RaiseBoardGlobalResponse>(GLOBAL_KEY)
        : undefined;
      const prevVotes = queryClient.getQueryData<string[]>(votesKey);
      const prevBySlug = scopedKey
        ? queryClient.getQueryData<RaiseBoardBySlugResponse>(scopedKey)
        : undefined;

      const wasVoted = (prevVotes ?? []).includes(applicationId);
      const delta = wasVoted ? -1 : 1;

      writeCount(applicationId, (c) => c + delta);

      queryClient.setQueryData<string[]>(votesKey, (old) => {
        const list = old ?? [];
        return wasVoted
          ? list.filter((id) => id !== applicationId)
          : [...list, applicationId];
      });

      return { prevGlobal, prevVotes, prevBySlug };
    },
    // The server recomputes the count from the vote collection and returns it,
    // so take that number rather than living with the guessed delta until the
    // refetch lands.
    onSuccess: (res, applicationId) => {
      writeCount(applicationId, () => res.upvote_count);
      queryClient.setQueryData<string[]>(votesKey, (old) => {
        const list = (old ?? []).filter((id) => id !== applicationId);
        return res.voted ? [...list, applicationId] : list;
      });
    },
    onError: (e, _applicationId, context) => {
      if (context?.prevGlobal !== undefined) {
        queryClient.setQueryData(GLOBAL_KEY, context.prevGlobal);
      }
      if (context?.prevVotes !== undefined) {
        queryClient.setQueryData(votesKey, context.prevVotes);
      }
      if (scopedKey && context?.prevBySlug !== undefined) {
        queryClient.setQueryData(scopedKey, context.prevBySlug);
      }
      toast.error(getErrorMessage(e, "Failed to register your vote"));
    },
    // Counts are already exact by here; this refetch is for the ranking, which
    // only the server can reorder.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: votesKey });
      if (touchesGlobal) {
        queryClient.invalidateQueries({ queryKey: GLOBAL_KEY });
      }
      if (scopedKey) queryClient.invalidateQueries({ queryKey: scopedKey });
    },
  });
}

/**
 * Adds a raise-board company to the signed-in user's active workspace and
 * opens the new deal in a new tab. Callers are expected to gate this behind
 * their own sign-in check (e.g. BoardAuthDialog) before invoking it; the
 * openSignIn() call below is just a defensive fallback that shouldn't
 * normally be reached.
 */
export function useAddToPipeline() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { openSignIn } = useClerk();
  const { activeWorkspaceId } = useWorkspaceStore();

  return useMutation({
    mutationFn: async (companyId: string) => {
      if (!isLoaded) throw new Error("Auth still loading");
      if (!isSignedIn || !user?.id) {
        openSignIn();
        throw new Error("redirect-to-trial");
      }
      if (!activeWorkspaceId) {
        throw new Error(
          "Select a workspace in the main app before adding deals here."
        );
      }
      const { data: response } = await axiosInstance.post(
        "/create_opportunity_application_explore",
        {
          applicant_company: companyId,
          pertains_to_opportunity: activeWorkspaceId,
          changed_by: user.id,
          source: "GlobalRaiseBoard",
        }
      );
      return response;
    },
    // The deal-detail route resolves its :id as the applicant COMPANY id
    // (fetch_single_deal queries by company_id), not the application id the
    // create endpoint returns — opening result._id lands on "Deal not found".
    onSuccess: (_response, companyId) => {
      toast.success("Added to your pipeline.");
      if (activeWorkspaceId && companyId) {
        window.open(
          `/${activeWorkspaceId}/deals/${companyId}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    },
    onError: (e: unknown) => {
      if ((e as Error)?.message === "redirect-to-trial") return;
      toast.error(getErrorMessage(e, "Failed to add to your pipeline"));
    },
  });
}

/**
 * Board subscriptions are per-board, so the cache key carries the board.
 *
 * Deliberately not myVotesKey's shape. That one collapses every non-lookbook
 * scope to the literal "global", which is right for votes — the public boards
 * and /global share one tally, so one cache entry is the truth for all of
 * them. Subscriptions are not shared that way: /rb/acme and /rb/beta are
 * different subscriptions, and reusing that key would show you as subscribed
 * to a board you have never opened.
 *
 * The user id segment is kept for the reason documented on myVotesKey: the
 * board sign-in dialog completes without a reload, nothing clears the query
 * cache on auth change, and gcTime is 30 minutes.
 */
function mySubscriptionKey(
  userId: string | null | undefined,
  scope: BoardScope
) {
  return [
    "raise-board-my-subscription",
    userId ?? "anon",
    scope.lookbookPublicId ? `lb:${scope.lookbookPublicId}` : `rb:${scope.slug}`,
  ] as const;
}

/** Whether the signed-in user is subscribed to this board. */
export function useMySubscription(enabled: boolean, scope: BoardScope): boolean {
  const { user } = useUser();
  const hasBoard = !!(scope.lookbookPublicId || scope.slug);
  const { data } = useQuery({
    queryKey: mySubscriptionKey(user?.id, scope),
    queryFn: () => raiseBoardApi.getMySubscription(scope),
    // Signed out there is nothing to read, and the "anon" key keeps a visitor
    // from seeing whatever the last account cached.
    enabled: enabled && hasBoard && !!user?.id,
  });
  return !!data;
}

/**
 * Subscribes to, or unsubscribes from, one board. Flips optimistically so the
 * button responds immediately, and rolls back if the server disagrees.
 */
export function useToggleSubscription(scope: BoardScope) {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const key = mySubscriptionKey(user?.id, scope);

  return useMutation({
    mutationFn: () => raiseBoardApi.toggleSubscription(scope),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<boolean>(key);
      queryClient.setQueryData<boolean>(key, !previous);
      return { previous };
    },
    onSuccess: (response) => {
      queryClient.setQueryData<boolean>(key, response.subscribed);
      toast.success(
        response.subscribed
          ? "Subscribed. We'll email you when a new deal is added."
          : "Unsubscribed."
      );
    },
    onError: (e, _vars, context) => {
      queryClient.setQueryData<boolean>(key, context?.previous);
      toast.error(getErrorMessage(e, "Failed to update your subscription"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * The unsubscribe link in a board email points back at the board page with
 * ?unsubscribe=<token>, rather than at an API-hosted page: there is no
 * BACKEND_URL to build one from, and this way the reader lands on the board
 * they left. The token is the credential, so this works signed out.
 */
export function useUnsubscribeFromEmail(scope: BoardScope) {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: (token: string) => raiseBoardApi.unsubscribe(token),
    onSuccess: () => {
      queryClient.setQueryData<boolean>(
        mySubscriptionKey(user?.id, scope),
        false
      );
      toast.success("You've been unsubscribed from this board.");
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, "Failed to unsubscribe"));
    },
  });
}
